import { createApi } from "@reduxjs/toolkit/query/react";
import * as hashes from "common/hashes";
import * as logger from "common/logger";
import * as config from "config";
import * as utils from "utils";

const baseQuery = async (args, api, extraOptions) => {
  try {
    const response = await utils.authenticatedFetch(
      config.endpoints.analytics(args.url),
      args.payload || {}
    );
    return {
      data: await response.json(),
    };
  } catch (e) {
    logger.exception(e);
    return {
      error: String(e),
    };
  }
};

export const analyticsApi = createApi({
  reducerPath: "analyticsApi",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    submissionAnalytics: builder.query({
      queryFn: async ({ submission, engine, metric }) => {
        const result = await utils.authenticatedFetch(
          config.endpoints.analytics("calculate-submission-analytics"),
          {
            body: JSON.stringify({
              submission,
              engine,
              metric,
            }),
            method: "post",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        let responseJson = await result.json();

        if (responseJson.presignedUrl) {
          const presignedResult = await fetch(responseJson.presignedUrl);
          responseJson = await presignedResult.json();
        }

        const submissionHash = await hashes.hashDict(submission);
        return {
          data: {
            ...responseJson,
            submissionHash,
          },
        };
      },
    }),
    rateChangeAnalytics: builder.query({
      query: ({ submission, engine, args }) => ({
        url: "calculate-rate-change-analytics",
        payload: {
          body: JSON.stringify({
            submission,
            engine,
            args,
          }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
    }),
    peerAnalytics: builder.query({
      queryFn: async ({ submission, engine, exposure }) => {
        const result = await utils.authenticatedFetch(
          config.endpoints.analytics("calculate-peer-analytics"),
          {
            body: JSON.stringify({
              submission,
              engine,
              exposure,
            }),
            method: "post",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        const responseJson = await result.json();
        return {
          data: {
            ...responseJson,
          },
        };
      },
    }),
    engineConfig: builder.query({
      queryFn: async ({ engine }) => {
        const result = await utils.authenticatedFetch(
          config.endpoints.analytics("retrieve-engine-config"),
          {
            body: JSON.stringify({ engine }),
            method: "post",
            headers: { "Content-Type": "application/json" },
          }
        );
        const responseJson = await result.json();
        return { data: responseJson };
      },
    }),
    // uses baseQuery so transport failures surface as `error` instead of throwing
    listPricingModels: builder.query({
      query: () => ({
        url: "list-pricing-models",
        payload: {
          method: "post",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        },
      }),
    }),
  }),
});

export const {
  useSubmissionAnalyticsQuery,
  useLazySubmissionAnalyticsQuery,
  useLazyRateChangeAnalyticsQuery,
  usePeerAnalyticsQuery,
  useEngineConfigQuery,
  useLazyEngineConfigQuery,
  useListPricingModelsQuery,
} = analyticsApi;
